import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import auth from "../utils/auth";
import "../styles/Orders.css";
import { scrollToTop } from "../hooks/useScrollToTop";

const Orders = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [ordersPerPage] = useState(3);
  const [statusFilter, setStatusFilter] = useState("all");
  const [archivedFilter, setArchivedFilter] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("date");
  const [sortDirection, setSortDirection] = useState("desc");

  const fetchAllOrders = useCallback(async () => {
    if (!auth.isAuthenticated() || auth.getUserRole() !== "admin") {
      auth.logout();
      navigate("/logga-in");
      scrollToTop();
      return;
    }

    try {
      setIsLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/admin/orders`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${auth.getToken()}`
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch orders: ${response.status} ${response.statusText}`);
      }

      const ordersData = await response.json();
      setOrders(ordersData);
    } catch (error) {
      setError(error.message || "Det gick inte att hämta beställningar. Försök igen senare.");
    } finally {
      setIsLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchAllOrders();
  }, [fetchAllOrders]);

  const handleStatusChange = async (orderId, newStatus) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/orders/${orderId}/status`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${auth.getToken()}`
          },
          body: JSON.stringify({ status: newStatus })
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to update status: ${response.status} ${response.statusText}`);
      }

      setOrders(
        orders.map((order) =>
          order.orderId === orderId ? { ...order, status: newStatus } : order
        )
      );
    } catch (error) {
      setError(error.message || "Det gick inte att uppdatera orderstatus. Försök igen senare.");
    }
  };

  const handleArchiveToggle = async (orderId, currentArchived) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/admin/orders/${orderId}/archive`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${auth.getToken()}`
          },
          body: JSON.stringify({ archived: !currentArchived })
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to update archive status: ${response.status} ${response.statusText}`);
      }

      setOrders(
        orders.map((order) =>
          order.orderId === orderId ? { ...order, archived: !currentArchived } : order
        )
      );
    } catch (error) {
      setError(error.message || "Det gick inte att arkivera/återställa ordern. Försök igen senare.");
    }
  };

  const handleDeleteOrder = async (orderId) => {
    if (!window.confirm(`Är du säker på att du vill radera order "${orderId}"? Denna åtgärd kan inte ångras.`)) {
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/admin/orders/${orderId}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${auth.getToken()}`
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to delete order: ${response.status} ${response.statusText}`);
      }

      setOrders(orders.filter((order) => order.orderId !== orderId));
    } catch (error) {
      setError(error.message || "Det gick inte att radera ordern. Försök igen senare.");
    }
  };

  const getStatusClass = (status) => {
    switch (status) {
      case "pending":
        return "orders-status-pending";
      case "shipped":
        return "orders-status-shipped";
      case "returned":
        return "orders-status-returned";
      default:
        return "";
    }
  };

  const formatDate = (dateString) => {
    const options = { year: "numeric", month: "long", day: "numeric", hour: "2-digit", minute: "2-digit" };
    return new Date(dateString).toLocaleDateString("sv-SE", options);
  };

  const calculateSubtotal = (order) => {
    if (!order || !order.orderItems || !Array.isArray(order.orderItems) || order.orderItems.length === 0) {
      return 0;
    }
    return order.orderItems.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const calculateTotal = (order) => {
    if (!order) {
      return 0;
    }

    const subtotal = calculateSubtotal(order);

    let shippingCost = 65;

    if (order.shippingCost !== undefined) {
      shippingCost = order.shippingCost;
    }

    else if (order.orderItems.length > 0 && order.orderItems[0].shippingCost !== undefined) {
      shippingCost = order.orderItems[0].shippingCost || 0;
    }

    return subtotal + shippingCost;
  };

  const handleOrderClick = (orderId) => {
    navigate(`/order/${orderId}`);
    scrollToTop();
  };

  const handleSortChange = (field) => {
    if (sortBy === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortDirection("asc");
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleFilterChange = (e) => {
    setStatusFilter(e.target.value);
    setCurrentPage(1);
  };

  const handleArchivedFilterChange = (e) => {
    setArchivedFilter(e.target.checked);
    setCurrentPage(1);
  };

  const resetFilters = () => {
    setStatusFilter("all");
    setArchivedFilter(false);
    setSearchTerm("");
    setCurrentPage(1);
  };

  const sortedOrders = [...orders].sort((a, b) => {
    switch (sortBy) {
      case "date":
        return sortDirection === "asc"
          ? new Date(a.createdAt) - new Date(b.createdAt)
          : new Date(b.createdAt) - new Date(a.createdAt);
      case "id":
        return sortDirection === "asc"
          ? a.orderId.localeCompare(b.orderId)
          : b.orderId.localeCompare(a.orderId);
      case "status":
        return sortDirection === "asc"
          ? a.status.localeCompare(b.status)
          : b.status.localeCompare(a.status);
      case "total":
        const totalA = calculateTotal(a);
        const totalB = calculateTotal(b);
        return sortDirection === "asc" ? totalA - totalB : totalB - totalA;
      default:
        return 0;
    }
  });

  const filteredOrders = sortedOrders.filter((order) => {
    if (statusFilter !== "all" && order.status !== statusFilter) {
      return false;
    }

    if (archivedFilter !== order.archived) {
      return false;
    }

    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      const customerEmail = order.isGuestOrder ? order.email : order.user?.email;
      const customerName = order.isGuestOrder ? "" : order.user?.profile?.name || "";

      if (
        !order.orderId.toLowerCase().includes(searchLower) &&
        !(customerEmail && customerEmail.toLowerCase().includes(searchLower)) &&
        !(customerName && customerName.toLowerCase().includes(searchLower)) &&
        !order.shippingAddress.street.toLowerCase().includes(searchLower) &&
        !order.shippingAddress.postalCode.toLowerCase().includes(searchLower) &&
        !order.shippingAddress.region.toLowerCase().includes(searchLower)
      ) {
        return false;
      }
    }

    return true;
  });

  const activeOrders = orders.filter(order => !order.archived);
  const hasActiveOrders = activeOrders.length > 0;

  const shouldDisplayOrders = archivedFilter ? filteredOrders.length > 0 : hasActiveOrders;

  const indexOfLastOrder = currentPage * ordersPerPage;
  const indexOfFirstOrder = indexOfLastOrder - ordersPerPage;
  const currentOrders = filteredOrders.slice(indexOfFirstOrder, indexOfLastOrder);
  const totalPages = Math.ceil(filteredOrders.length / ordersPerPage);

  const paginate = (pageNumber) => {
    const validPage = Math.max(1, Math.min(pageNumber, totalPages));
    setCurrentPage(validPage);
    scrollToTop();
  };

  const jumpBackward = () => {
    const newPage = Math.max(1, currentPage - 10);
    setCurrentPage(newPage);
    scrollToTop();
  };

  const jumpForward = () => {
    const newPage = Math.min(totalPages, currentPage + 10);
    setCurrentPage(newPage);
    scrollToTop();
  };

  if (isLoading) {
    return <div className="orders-loading">Laddar beställningar...</div>;
  }

  return (
    <div className="orders-container">
      <div className="orders-header">
        <h2 className="orders-title">Orderhantering</h2>
        <div className="orders-controls">
          <div className="orders-search">
            <input
              type="text"
              placeholder="Sök efter order ID, namn, email, adress..."
              value={searchTerm}
              onChange={handleSearch}
              className="orders-search-input"
            />
          </div>
          <div className="orders-filters">
            <select
              value={statusFilter}
              onChange={handleFilterChange}
              className="orders-filter-select"
            >
              <option value="all">Alla statusar</option>
              <option value="pending">Behandlas</option>
              <option value="shipped">Skickad</option>
              <option value="returned">Returnerad</option>
            </select>
            <div className="orders-archive-filter">
              <input
                type="checkbox"
                id="archivedFilter"
                checked={archivedFilter}
                onChange={handleArchivedFilterChange}
              />
              <label htmlFor="archivedFilter">Visa arkiverade</label>
            </div>
            <button onClick={resetFilters} className="orders-reset-button">
              Återställ filter
            </button>
          </div>
        </div>
      </div>

      {error && <p className="orders-error">{error}</p>}

      {!shouldDisplayOrders ? (
        <p className="orders-no-orders">Inga beställningar hittades.</p>
      ) : (
        <>
          <div className="orders-table-container">
            <table className="orders-table">
              <thead>
                <tr>
                  <th
                    onClick={() => handleSortChange("id")}
                    className={sortBy === "id" ? "orders-sort-active" : ""}
                  >
                    Order ID
                    {sortBy === "id" && (
                      <span className="orders-sort-indicator">
                        {sortDirection === "asc" ? " ▲" : " ▼"}
                      </span>
                    )}
                  </th>
                  <th
                    onClick={() => handleSortChange("date")}
                    className={sortBy === "date" ? "orders-sort-active" : ""}
                  >
                    Datum
                    {sortBy === "date" && (
                      <span className="orders-sort-indicator">
                        {sortDirection === "asc" ? " ▲" : " ▼"}
                      </span>
                    )}
                  </th>
                  <th>Kund</th>
                  <th
                    onClick={() => handleSortChange("status")}
                    className={sortBy === "status" ? "orders-sort-active" : ""}
                  >
                    Status
                    {sortBy === "status" && (
                      <span className="orders-sort-indicator">
                        {sortDirection === "asc" ? " ▲" : " ▼"}
                      </span>
                    )}
                  </th>
                  <th
                    onClick={() => handleSortChange("total")}
                    className={sortBy === "total" ? "orders-sort-active" : ""}
                  >
                    Totalt
                    {sortBy === "total" && (
                      <span className="orders-sort-indicator">
                        {sortDirection === "asc" ? " ▲" : " ▼"}
                      </span>
                    )}
                  </th>
                  <th>Åtgärder</th>
                </tr>
              </thead>
              <tbody>
                {currentOrders.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="orders-no-results">
                      Inga beställningar matchar dina filter.
                    </td>
                  </tr>
                ) : (
                  currentOrders.map((order) => (
                    <tr
                      key={order._id}
                      className={order.archived ? "orders-item-archived" : "orders-item"}
                    >
                      <td
                        className="orders-id"
                      >
                        #{order.orderId}
                      </td>
                      <td>{formatDate(order.createdAt)}</td>
                      <td>
                        {order.isGuestOrder ?
                          order.email :
                          `${order.user?.email}${order.user?.profile?.name ? ` | ${order.user.profile.name}` : ''}`
                        }
                        <div className="orders-address-preview">
                          {order.shippingAddress.careOf && <span>C/O {order.shippingAddress.careOf}, </span>}
                          <span>{order.shippingAddress.street}, {order.shippingAddress.postalCode} {order.shippingAddress.region}</span>
                        </div>
                      </td>
                      <td>
                        <select
                          className={`orders-status-dropdown ${getStatusClass(order.status)}`}
                          value={order.status}
                          onChange={(e) => handleStatusChange(order.orderId, e.target.value)}
                        >
                          <option value="pending">Behandlas</option>
                          <option value="shipped">Skickad</option>
                          <option value="returned">Returnerad</option>
                        </select>
                      </td>
                      <td className="orders-total">
                        {calculateTotal(order)} kr
                      </td>
                      <td>
                        <div className="orders-actions">
                          <button
                            onClick={() => handleOrderClick(order.orderId)}
                            className="orders-view-button"
                          >
                            Visa
                          </button>
                          <button
                            onClick={() => handleArchiveToggle(order.orderId, order.archived)}
                            className={order.archived ? "orders-unarchive-button" : "orders-archive-button"}
                          >
                            {order.archived ? "Avarkivera" : "Arkivera"}
                          </button>
                          <button
                            onClick={() => handleDeleteOrder(order.orderId)}
                            className="orders-delete-button"
                          >
                            Radera
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="orders-pagination">
              <button
                onClick={jumpBackward}
                disabled={currentPage <= 1}
                className="orders-pagination-button"
              >
                &larr; 10
              </button>
              <button
                onClick={() => paginate(currentPage - 1)}
                disabled={currentPage === 1}
                className="orders-pagination-button"
              >
                Föregående
              </button>
              <div className="orders-page-indicator">
                {currentPage} / {totalPages}
              </div>
              <button
                onClick={() => paginate(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="orders-pagination-button"
              >
                Nästa
              </button>
              <button
                onClick={jumpForward}
                disabled={currentPage >= totalPages}
                className="orders-pagination-button"
              >
                10 &rarr;
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Orders;